import {
    Architecture, MobilePlatforms, Platform, PlatformArchitectures, PlatformFormats, PlatformRequirements, Platforms,
    PluginFormat, PluginFormats, ProductRequirements
} from "../api/models";
import {format, language, products} from "../i18n";

const messages = products.requirements;
const andList = new Intl.ListFormat(language, { type: 'conjunction' });
const APPLE: readonly Platform[] = ['macos', 'ios', 'ipados'];

// Whether there's anything to show: the product's page has no System requirements section without.
export function hasRequirements(requirements: ProductRequirements | null | undefined): requirements is ProductRequirements {
    return !!requirements && (requirements.platforms.length > 0 ||
        [requirements.processor, requirements.memory, requirements.disk, requirements.other, requirements.notAvailable].some(x => x?.trim()));
}

// Its platforms in the usual order: desktop, then mobile.
export function sortedPlatforms(requirements: ProductRequirements): PlatformRequirements[] {
    return [...requirements.platforms].sort((a, b) => Platforms.indexOf(a.platform) - Platforms.indexOf(b.platform));
}

export const isMobile = (platform: Platform) => MobilePlatforms.includes(platform);

// Bootstrap Icons, as OperatingSystemLabel's for the desktop platforms.
export const platformIcons: Record<Platform, string> = {
    windows: 'bi-windows',
    macos: 'bi-apple',
    linux: 'bi-tux',
    ios: 'bi-phone',
    ipados: 'bi-tablet',
    android: 'bi-android2'
};

// What isn't available, worked out from the platforms so it can't fall out of step with them: platforms there's no
// version for (desktop ones, then mobile ones), formats some supported platform could have but none has, and
// processors a supported platform could run on but doesn't. Nothing is missing until there's a platform: a product
// whose requirements are only texts says nothing about the rest.
export interface RequirementGaps {
    desktopPlatforms: Platform[];
    mobilePlatforms: Platform[];
    formats: PluginFormat[];
    architectures: { platform: Platform, architecture: Architecture }[];
    // The administrator's own words, for what can't be worked out.
    notAvailable: string;
}

export function requirementGaps(requirements: ProductRequirements): RequirementGaps {
    const notAvailable = requirements.notAvailable?.trim() ?? '';
    const supported = sortedPlatforms(requirements);
    if (!supported.length) {
        return { desktopPlatforms: [], mobilePlatforms: [], formats: [], architectures: [], notAvailable };
    }
    const has = new Set(supported.map(x => x.platform));
    const missing = Platforms.filter(x => !has.has(x));
    const possible = new Set(supported.flatMap(x => PlatformFormats[x.platform]));
    const present = new Set(supported.flatMap(x => x.formats));
    return {
        desktopPlatforms: missing.filter(x => !isMobile(x)),
        mobilePlatforms: missing.filter(isMobile),
        formats: PluginFormats.filter(x => possible.has(x) && !present.has(x)),
        architectures: supported.flatMap(x => PlatformArchitectures[x.platform]
            .filter(a => !x.architectures.includes(a))
            .map(architecture => ({ platform: x.platform, architecture }))),
        notAvailable
    };
}

export const hasGaps = (gaps: RequirementGaps) =>
    !!(gaps.desktopPlatforms.length || gaps.mobilePlatforms.length || gaps.formats.length || gaps.architectures.length || gaps.notAvailable);

// "Intel", "Apple silicon" on Apple's platforms; "Intel/AMD (x64)", "ARM (arm64)" elsewhere.
export function architectureName(platform: Platform, architecture: Architecture): string {
    return (APPLE.includes(platform) ? messages.appleArchitectures : messages.architectures)[architecture];
}

// The lines under Not available, each a label and its list.
export function gapLines(gaps: RequirementGaps): { label: string, text: string }[] {
    const platforms = [...gaps.desktopPlatforms, ...gaps.mobilePlatforms];
    return [
        ...(platforms.length ? [{ label: messages.missingPlatforms, text: andList.format(platforms.map(x => messages.platforms[x])) }] : []),
        ...(gaps.formats.length ? [{ label: messages.missingFormats, text: andList.format(gaps.formats.map(x => messages.formatNames[x])) }] : []),
        ...(gaps.architectures.length ? [{
            label: messages.missingArchitectures,
            text: andList.format(gaps.architectures.map(x => format(messages.platformOn, {
                platform: messages.platforms[x.platform], processor: architectureName(x.platform, x.architecture)
            })))
        }] : [])
    ];
}

// Every format any of its platforms has, in the usual order.
export function allFormats(requirements: ProductRequirements): PluginFormat[] {
    const present = new Set(requirements.platforms.flatMap(x => x.formats));
    return PluginFormats.filter(x => present.has(x));
}

// Which store a link goes to, for its button.
export function storeName(url: string): string | undefined {
    try {
        const host = new URL(url).hostname;
        if (host === 'apps.apple.com' || host.endsWith('.apple.com')) return messages.stores.apple;
        if (host === 'play.google.com') return messages.stores.google;
    } catch {
        // Not a URL: the API wouldn't have taken it.
    }
    return undefined;
}
